import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";

import { useStepSecondaryAddresseeStore } from "#src/stores/step-secondary-addressee.ts";
import { useStepExistingCoverageStore } from "#src/stores/step-existing-coverage.ts";
import { useInsuredStore } from "#src/stores/insured.ts";

import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { HttpDoctorVisitsService } from "#src/services/http-doctor-visits.service.js";
import { doctorVisitKeygen, useDoctorVisit } from "#src/stores/doctor-visit.js";
import { type Pinia, type StoreGeneric } from "pinia";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";
import { computed } from "vue";
import { type MedicalSource, type InsuredType } from "#src/types/Insured.ts";
import {
  startHumanAPISession,
  updateHumanAPIMedicalSources,
} from "#src/services/http-individuals.service.ts";

interface PhysicianStep extends BaseStepStore, ToSetupState<PhysicianState> {
  humanSession(insuredType: InsuredType): Promise<string>;
  updateMedicalSources(insuredType: InsuredType): Promise<unknown>;
  addDoctorVisit(insuredType: InsuredType): Promise<string>;
  setStepRequirements(srq: DataRequirements): void;
  removeDoctorVisit(
    insuredType: InsuredType,
    doctorVisitId: string
  ): Promise<void>;
}

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;

  return {
    humanAPI: required(REQS.HUMAN_API),
    doctorVisits: required(REQS.DOCTOR_VISITS),
  };
}

interface PhysicianState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}

function initialState(): PhysicianState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

function initializePhysicianStep(pinia: Pinia): PhysicianStep {
  const state = generateInitialState<PhysicianState>(initialState());

  function $reset() {
    resetState<PhysicianState>(state, initialState());
  }

  const step = computed(() => STEP.PHYSICIAN);
  const next = computed(() => {
    const secondaryAddressee = useStepSecondaryAddresseeStore(pinia);
    if (secondaryAddressee.inactive) return secondaryAddressee.next;
    return secondaryAddressee.step;
  });
  const previous = computed(() => {
    const existing = useStepExistingCoverageStore(pinia);
    if (existing.inactive) return existing.previous;
    return existing.step;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(srq);
    state.inactive.value = !Object.values(state.requiredFields.value).includes(
      true
    );
  }

  function humanSession(insuredType: InsuredType) {
    const insured = useInsuredStore(insuredType, pinia);
    return startHumanAPISession(pinia, insured.id);
  }

  function updateMedicalSources(insuredType: InsuredType) {
    const insured = useInsuredStore(insuredType, pinia);
    const currentConnections: MedicalSource[] = [];
    const requestedSources: MedicalSource[] = [];
    insured.medical_sources.forEach((value: MedicalSource) => {
      if (value.name) currentConnections.push(value);
      else requestedSources.push(value);
    });
    return updateHumanAPIMedicalSources(
      pinia,
      insured.id,
      requestedSources,
      currentConnections
    );
  }

  async function addDoctorVisit(insuredType: InsuredType) {
    const insured = useInsuredStore(insuredType, pinia);
    const id = await new HttpDoctorVisitsService(pinia).createDoctorVisit({
      insured_id: insured.id,
      insured_type: "Individual",
    });

    const key = doctorVisitKeygen(id);
    insured.doctor_visits.push(key);

    const doctorVisit = useDoctorVisit(key, pinia);
    doctorVisit.insuredType = insuredType;
    doctorVisit.id = id;
    return key;
  }

  async function removeDoctorVisit(
    insuredType: InsuredType,
    doctorVisitId: string
  ) {
    const insured = useInsuredStore(insuredType, pinia);
    const doctorVisit = useDoctorVisit(doctorVisitId, pinia);
    await new HttpDoctorVisitsService(pinia).deleteDoctorVisit(doctorVisitId);
    insured.doctor_visits.splice(
      insured.doctor_visits.indexOf(doctorVisitId),
      1
    );
    doctorVisit.$dispose();
  }

  return {
    ...state,
    $reset,
    step,
    next,
    previous,
    setStepRequirements,
    humanSession,
    updateMedicalSources,
    addDoctorVisit,
    removeDoctorVisit,
  };
}

export const useStepPhysicianStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, PhysicianStep>("step-physician", () => {
    return initializePhysicianStep(pinia);
  })(pinia, hot);
};
